import React, { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import MainContainer from "../../components/MainContainer";
import AppPageTitle from "../../components/AppPageTitle";
import AppTableActionMenu from "../../components/AppTableActionMenu";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import QueueModal from "../../components/QueueModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import PermissionModal from "../../components/PermissionModal";
import { AuthContext } from "../../context/Auth/AuthContext";

const PAGE_SIZE = 10;

const reducer = (state, action) => {
	switch (action.type) {
		case "SET_QUEUES":
			return action.payload || [];
		case "UPSERT_QUEUE": {
			const nextQueue = action.payload;
			const existingIndex = state.findIndex(item => item.id === nextQueue.id);

			if (existingIndex >= 0) {
				const clone = [...state];
				clone[existingIndex] = {
					...clone[existingIndex],
					...nextQueue,
				};
				return clone;
			}

			return [nextQueue, ...state];
		}
		case "DELETE_QUEUE":
			return state.filter(item => String(item.id) !== String(action.payload));
		default:
			return state;
	}
};

function IconSearch() {
	return (
		<svg className="size-4 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconPlus({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 5v14M5 12h14" />
		</svg>
	);
}

function IconMore() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="1" />
			<circle cx="19" cy="12" r="1" />
			<circle cx="5" cy="12" r="1" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 20h9" />
			<path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
		</svg>
	);
}

function IconDelete() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function IconChevronLeft({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="15 18 9 12 15 6" />
		</svg>
	);
}

function IconChevronRight({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="9 18 15 12 9 6" />
		</svg>
	);
}

function IconLayers() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m12 3-9 4.5 9 4.5 9-4.5L12 3Z" />
			<path d="m3 12 9 4.5 9-4.5" />
			<path d="m3 16.5 9 4.5 9-4.5" />
		</svg>
	);
}

function IconUsers() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
			<circle cx="9" cy="7" r="4" />
			<path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
		</svg>
	);
}

function IconLink() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07L11.7 5.24" />
			<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.77-1.76" />
		</svg>
	);
}

function SummaryCard({ title, value, hint, icon, toneClassName }) {
	return (
		<div className="card h-full">
			<div className="card-body flex items-start justify-between gap-4">
				<div>
					<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
						{title}
					</p>
					<p className="m-0 text-2xl font-semibold text-default-800 dark:text-default-100">
						{value}
					</p>
					<p className="mt-2 mb-0 text-sm text-default-500 dark:text-default-400">
						{hint}
					</p>
				</div>
				<div
					className={`flex size-12 shrink-0 items-center justify-center rounded-2xl ${toneClassName}`}
				>
					{icon}
				</div>
			</div>
		</div>
	);
}

function QueueColorBadge({ color }) {
	const hex = color || "#71717a";

	return (
		<span
			className="inline-flex items-center gap-x-1.5 rounded px-2.5 py-0.5 text-xs font-medium"
			style={{
				backgroundColor: `${hex}1a`,
				color: hex,
				border: `1px solid ${hex}40`,
			}}
			title={hex}
		>
			<span
				className="size-2.5 shrink-0 rounded-full"
				style={{ backgroundColor: hex }}
				aria-hidden
			/>
			{hex.toUpperCase()}
		</span>
	);
}

function QueueRowMenu({ onEdit, onDelete }) {
	return (
		<AppTableActionMenu
			triggerIcon={<IconMore />}
			triggerAriaLabel={i18n.t("queues.table.actions")}
			minWidth={144}
			items={[
				{
					key: "edit",
					label: "Editar",
					icon: <IconEdit />,
					onClick: onEdit,
				},
				{
					key: "delete",
					label: "Excluir",
					icon: <IconDelete />,
					tone: "danger",
					onClick: onDelete,
				},
			]}
		/>
	);
}

function normalizeText(value) {
	return typeof value === "string" ? value.trim() : "";
}

function formatDaysWork(dayWork) {
	let parsed = dayWork;

	if (typeof parsed === "string") {
		try {
			parsed = JSON.parse(parsed);
		} catch {
			parsed = parsed.split(",").map(item => item.trim()).filter(Boolean);
		}
	}

	if (!Array.isArray(parsed) || parsed.length === 0) {
		return "—";
	}

	return parsed.join(", ");
}

function normalizeDayKey(value) {
	return String(value || "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.trim();
}

function parseDaySchedules(raw) {
	if (raw == null) return null;
	if (typeof raw === "string") {
		const trimmed = raw.trim();
		if (!trimmed) return null;
		try {
			return parseDaySchedules(JSON.parse(trimmed));
		} catch {
			return null;
		}
	}
	if (typeof raw !== "object" || Array.isArray(raw)) return null;
	return raw;
}

function formatExpediente(queue) {
	const schedules = parseDaySchedules(queue?.daySchedules);
	if (schedules) {
		const enabled = Object.entries(schedules)
			.map(([key, item]) => ({ key: normalizeDayKey(key), item }))
			.filter(({ item }) => item && item.enabled);
		if (enabled.length === 0) {
			return "Sem dias ativos";
		}
		const ranges = [
			...new Set(
				enabled.map(
					({ item }) =>
						`${String(item.start || "").slice(0, 5)} – ${String(item.end || "").slice(0, 5)}`
				)
			),
		];
		if (ranges.length === 1) {
			return ranges[0];
		}
		return `${ranges[0]} (+${ranges.length - 1})`;
	}
	return formatTimeRange(queue?.startWork, queue?.endWork);
}

function formatTimeRange(start, end) {
	if (!start && !end) return "—";
	if (start && end) return `${start} - ${end}`;
	return start || end || "—";
}

function summarizeNames(items = [], emptyText, suffix) {
	if (!Array.isArray(items) || items.length === 0) {
		return emptyText;
	}

	if (items.length <= 2) {
		return items.map(item => item.name).join(", ");
	}

	return `${items.length} ${suffix}`;
}

function QueuesTableSkeleton() {
	return (
		<>
			{[1, 2, 3, 4, 5, 6].map(row => (
				<tr key={row} className="animate-pulse">
					<td className="px-3.5 py-3">
						<div className="h-4 w-28 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="h-5 w-24 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="h-4 w-52 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="h-4 w-24 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="h-4 w-24 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="h-4 w-44 rounded bg-default-200 dark:bg-default-700" />
					</td>
					<td className="px-3.5 py-3">
						<div className="ms-auto size-7.5 rounded bg-default-200 dark:bg-default-700" />
					</td>
				</tr>
			))}
		</>
	);
}

export default function Queues() {
	const [queues, dispatch] = useReducer(reducer, []);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [pageNumber, setPageNumber] = useState(1);
	const [queueModalOpen, setQueueModalOpen] = useState(false);
	const [selectedQueue, setSelectedQueue] = useState(null);
	const [permissionOpen, setPermissionOpen] = useState(false);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		(async () => {
			setLoading(true);
			try {
				const { data } = await api.get("/queue");
				dispatch({ type: "SET_QUEUES", payload: data });
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	useEffect(() => {
		const socket = openSocket({
			scope: "queues",
			userId: user.id,
			component: "Queues",
			companyId: user.companyId,
		});

		socket.on("queue", data => {
			if (data.action === "update" || data.action === "create") {
				if (
					data.queue &&
					(data.queue.companyId == null || data.queue.companyId === user.companyId)
				) {
					dispatch({ type: "UPSERT_QUEUE", payload: data.queue });
				}
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user.id, user.companyId]);

	useEffect(() => {
		setPageNumber(1);
	}, [searchParam]);

	const filteredQueues = useMemo(() => {
		const term = searchParam.toLowerCase().trim();
		if (!term) {
			return [...queues].sort((a, b) => a.id - b.id);
		}

		return [...queues]
			.filter(queue =>
				[
					queue.id,
					queue.name,
					queue.color,
					queue.greetingMessage,
					queue.messageLunch,
					queue.startWork,
					queue.endWork,
					queue.startLunch,
					queue.endLunch,
					formatDaysWork(queue.dayWork),
				]
					.filter(Boolean)
					.join(" ")
					.toLowerCase()
					.includes(term)
			)
			.sort((a, b) => a.id - b.id);
	}, [queues, searchParam]);

	const totalPages = Math.max(1, Math.ceil(filteredQueues.length / PAGE_SIZE));

	const paginatedQueues = useMemo(() => {
		const start = (pageNumber - 1) * PAGE_SIZE;
		return filteredQueues.slice(start, start + PAGE_SIZE);
	}, [filteredQueues, pageNumber]);

	const rangeStart = filteredQueues.length ? (pageNumber - 1) * PAGE_SIZE + 1 : 0;
	const rangeEnd = Math.min(pageNumber * PAGE_SIZE, filteredQueues.length);

	const pageNumbers = () => {
		if (totalPages <= 1) return [1];

		let from = Math.max(1, pageNumber - 2);
		let to = Math.min(totalPages, from + 4);

		if (to - from < 4) {
			from = Math.max(1, to - 4);
		}

		return Array.from({ length: to - from + 1 }, (_, index) => from + index);
	};

	const summary = useMemo(() => {
		return {
			total: queues.length,
			filtered: filteredQueues.length,
			withUsers: queues.filter(queue => (queue.users || []).length > 0).length,
			withConnections: queues.filter(queue => (queue.whatsapps || []).length > 0).length,
		};
	}, [queues, filteredQueues.length]);

	const handleOpenQueueModal = () => {
		setSelectedQueue(null);
		setQueueModalOpen(true);
	};

	const handleCloseQueueModal = () => {
		setQueueModalOpen(false);
		setSelectedQueue(null);
	};

	const handleEditQueue = queue => {
		setSelectedQueue(queue);
		setQueueModalOpen(true);
	};

	const handleCloseConfirmationModal = () => {
		setConfirmModalOpen(false);
		setSelectedQueue(null);
	};

	const handleDeleteQueue = async queueId => {
		try {
			await api.delete(`/queue/${queueId}`);
			dispatch({ type: "DELETE_QUEUE", payload: queueId });
			toast.success(i18n.t("queues.notifications.queueDeleted"));
		} catch (err) {
			toastError(err);
		} finally {
			setSelectedQueue(null);
			setConfirmModalOpen(false);
		}
	};

	return (
		<MainContainer>
			<div className="app-admin-page">
				<PermissionModal open={permissionOpen} onClose={setPermissionOpen} />
				<ConfirmationModal
					title={
						selectedQueue
							? `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name}?`
							: i18n.t("queues.confirmationModal.deleteTitle")
					}
					open={confirmModalOpen}
					onClose={handleCloseConfirmationModal}
					onConfirm={() => handleDeleteQueue(selectedQueue.id)}
				>
					{i18n.t("queues.confirmationModal.deleteMessage")}
				</ConfirmationModal>
				<QueueModal
					open={queueModalOpen}
					onClose={handleCloseQueueModal}
					queueId={selectedQueue?.id}
				/>

				<div className="app-admin-page__scroll">
					<AppPageTitle title={i18n.t("queues.title")} subtitle="Administração" />

					<div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
						<SummaryCard
							title="Setores"
							value={summary.total}
							hint="Total de setores cadastrados."
							icon={<IconLayers />}
							toneClassName="bg-primary/10 text-primary"
						/>
						<SummaryCard
							title="Resultado atual"
							value={summary.filtered}
							hint="Setores encontrados pela busca."
							icon={<IconLayers />}
							toneClassName="bg-info/10 text-info"
						/>
						<SummaryCard
							title="Com usuários"
							value={summary.withUsers}
							hint="Setores com pelo menos um usuário vinculado."
							icon={<IconUsers />}
							toneClassName="bg-success/10 text-success"
						/>
						<SummaryCard
							title="Com conexões"
							value={summary.withConnections}
							hint="Setores ligados a pelo menos uma conexão."
							icon={<IconLink />}
							toneClassName="bg-warning/10 text-warning"
						/>
					</div>

					<div className="card">
						<div className="card-header flex-wrap items-start justify-between gap-3 px-5 pt-5 pb-4">
							<div>
								<h6 className="card-title m-0">{i18n.t("queues.title")}</h6>
								<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
									Visualize jornada, mensagens e vínculos operacionais de cada setor.
								</p>
							</div>
							<button
								type="button"
								className="btn btn-sm shrink-0 bg-primary text-white"
								onClick={handleOpenQueueModal}
							>
								<IconPlus />
								{i18n.t("queues.buttons.add")}
							</button>
						</div>

						<div className="card-header flex-wrap gap-3 px-5 py-4">
							<div className="relative w-full md:max-w-sm">
								<input
									type="search"
									className="form-input form-input-sm w-full ps-11"
									placeholder="Pesquisar setores..."
									value={searchParam}
									onChange={event => setSearchParam(event.target.value)}
								/>
								<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
									<IconSearch />
								</div>
							</div>
						</div>

						<div className="flex min-h-0 flex-1 flex-col">
							{loading ? (
								<div className="overflow-x-auto px-2 pt-2">
									<table className="min-w-full">
										<tbody>
											<QueuesTableSkeleton />
										</tbody>
									</table>
								</div>
							) : filteredQueues.length === 0 ? (
								<div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
									<p className="m-0 font-semibold text-default-800 dark:text-default-100">
										Nenhum setor encontrado
									</p>
									<p className="m-0 text-sm text-default-500 dark:text-default-400">
										{searchParam
											? "Tente ajustar a busca para localizar outro setor."
											: "Cadastre um novo setor para começar."}
									</p>
								</div>
							) : (
								<div className="flex min-h-0 flex-1 flex-col">
									<div className="app-table-scroll">
										<div className="inline-block min-w-full align-middle">
											<div className="overflow-visible">
												<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
													<thead className="bg-default-150 dark:bg-default-800/60">
														<tr className="text-sm font-normal text-default-700 dark:text-default-300">
															<th className="px-3.5 py-3 text-start">
																{i18n.t("queues.table.name")}
															</th>
															<th className="px-3.5 py-3 text-start">
																{i18n.t("queues.table.color")}
															</th>
															<th className="px-3.5 py-3 text-start">
																Mensagens
															</th>
															<th className="px-3.5 py-3 text-start">Jornada</th>
															<th className="px-3.5 py-3 text-start">Usuários</th>
															<th className="px-3.5 py-3 text-start">Conexões</th>
															<th className="px-3.5 py-3 text-center">
																{i18n.t("queues.table.actions")}
															</th>
														</tr>
													</thead>
													<tbody className="divide-y divide-default-200 dark:divide-default-700">
														{paginatedQueues.map(queue => (
															<tr
																key={queue.id}
																className="text-sm text-default-800 dark:text-default-200"
															>
																<td className="px-3.5 py-3">
																	<div className="min-w-[12rem]">
																		<h6 className="mb-0 truncate font-semibold text-default-800 dark:text-default-100">
																			{queue.name}
																		</h6>
																		<p className="m-0 text-xs text-default-500 dark:text-default-400">
																			ID #{queue.id}
																		</p>
																	</div>
																</td>
																<td className="px-3.5 py-3">
																	<QueueColorBadge color={queue.color} />
																</td>
																<td className="px-3.5 py-3">
																	<div className="min-w-[15rem] space-y-2">
																		<div>
																			<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
																				Saudação
																			</p>
																			<p className="m-0 max-w-[18rem] truncate text-default-700 dark:text-default-200">
																				{normalizeText(queue.greetingMessage) || "—"}
																			</p>
																		</div>
																		<div>
																			<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
																				Almoço
																			</p>
																			<p className="m-0 max-w-[18rem] truncate text-default-700 dark:text-default-200">
																				{normalizeText(queue.messageLunch) || "—"}
																			</p>
																		</div>
																	</div>
																</td>
																<td className="px-3.5 py-3">
																	<div className="min-w-[14rem] space-y-2">
																		<div>
																			<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
																				Expediente
																			</p>
																			<p className="m-0 text-default-700 dark:text-default-200">
																				{formatExpediente(queue)}
																			</p>
																		</div>
																		<div>
																			<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
																				Dias
																			</p>
																			<p className="m-0 max-w-[16rem] truncate text-default-700 dark:text-default-200">
																				{formatDaysWork(queue.dayWork)}
																			</p>
																		</div>
																		<div>
																			<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
																				Pausa para almoço
																			</p>
																			<p className="m-0 text-default-700 dark:text-default-200">
																				{formatTimeRange(queue.startLunch, queue.endLunch)}
																			</p>
																		</div>
																	</div>
																</td>
																<td className="px-3.5 py-3">
																	<div className="min-w-[12rem]">
																		<p className="m-0 font-medium text-default-800 dark:text-default-100">
																			{(queue.users || []).length}
																		</p>
																		<p className="mt-1 mb-0 text-xs text-default-500 dark:text-default-400">
																			{summarizeNames(
																				queue.users,
																				"Nenhum usuário vinculado",
																				"usuários vinculados"
																			)}
																		</p>
																	</div>
																</td>
																<td className="px-3.5 py-3">
																	<div className="min-w-[12rem]">
																		<p className="m-0 font-medium text-default-800 dark:text-default-100">
																			{(queue.whatsapps || []).length}
																		</p>
																		<p className="mt-1 mb-0 text-xs text-default-500 dark:text-default-400">
																			{summarizeNames(
																				queue.whatsapps,
																				"Nenhuma conexão vinculada",
																				"conexões vinculadas"
																			)}
																		</p>
																	</div>
																</td>
																<td className="px-3.5 py-3 text-center">
																	<div className="inline-flex justify-center">
																		<QueueRowMenu
																			onEdit={() => handleEditQueue(queue)}
																			onDelete={() => {
																				setSelectedQueue(queue);
																				user.profile === "admin"
																					? setConfirmModalOpen(true)
																					: setPermissionOpen(true);
																			}}
																		/>
																	</div>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										</div>
									</div>

									{filteredQueues.length > 0 && (
										<div className="card-footer">
											<p className="m-0 text-sm text-default-500">
												Exibindo {rangeStart} a {rangeEnd} de {filteredQueues.length} setor(es)
											</p>
											{totalPages > 1 && (
												<nav
													className="flex flex-wrap items-center gap-2"
													aria-label="Paginação de setores"
												>
													<button
														type="button"
														className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
														disabled={pageNumber <= 1}
														onClick={() =>
															setPageNumber(current => Math.max(1, current - 1))
														}
													>
														<IconChevronLeft className="size-4 me-1 shrink-0" />
														Anterior
													</button>
													{pageNumbers().map(page => (
														<button
															key={page}
															type="button"
															className={`btn size-7.5 shrink-0 border ${
																page === pageNumber
																	? "border-primary bg-primary text-white"
																	: "border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
															}`}
															onClick={() => setPageNumber(page)}
														>
															{page}
														</button>
													))}
													<button
														type="button"
														className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
														disabled={pageNumber >= totalPages}
														onClick={() =>
															setPageNumber(current =>
																Math.min(totalPages, current + 1)
															)
														}
													>
														Próxima
														<IconChevronRight className="size-4 ms-1 shrink-0" />
													</button>
												</nav>
											)}
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</MainContainer>
	);
}
